import { defineStore } from 'pinia'
import { mergeOptionsParams, useCreateForm, useEditForm } from "@/stores/_helpers.js";
import toasteventbus from "primevue/toasteventbus";

export const useAssetsStore = defineStore('assets', {
  state: () => ({
    data: undefined,
    options: undefined,
    lastRequestId: 0,
  }),
  getters: {
    getAssets: (state) => state.data?.data,
    getAssetOptions: (state) => state.options?.data,
  },
  actions: {
    /**
     * This method should be used to return as little data as possible for use in select boxes
     *
     * @param silent
     * @param params
     * @returns {Promise<any>}
     */
    async indexAssetOptions(params = {}, silent = true) {

      const requestId = (++this.lastRequestId);

      if (!silent)
        this.options = undefined;

      try {
        if (!this.options) {
          let qryParams = mergeOptionsParams(params, 'assets', ['id','name','company_id']);
          const response = await axios.get('assets', {params: qryParams});
          if (requestId === this.lastRequestId) { // discard out of date data
            this.options = response.data;
          }
        }

        return this.options;
      } catch (error) {
        console.error(error.response);
        throw error.response;
      }
    },
    async indexAssets(params = {}) {
      try {
        const response = await axios.get('assets', {params});
        this.data = response.data;
        return response.data;
      } catch (error) {
        console.error(error.response);
        throw error.response;
      }
    },
    async indexRecentlyViewedAssets(params = {}) {
      try {
        const response = await axios.get('assets-recently-viewed', {params});
        return response.data;
      } catch (error) {
        console.error(error.response);
        throw error.response;
      }
    },
    async createAsset(params = {}) {
      try {
        const response = await axios.get('assets/create', {params});
        return useCreateForm('assets', response.data, { onFinish: () => this.options = undefined });
      } catch (error) {
        console.error(error.response);
        throw error.response;
      }
    },
    async showAsset(assetId, params = {}) {
      try {
        const response = await axios.get(`assets/${ assetId }`, {params});
        return response.data;
      } catch (error) {
        console.error(error.response);
        throw error.response;
      }
    },
    async editAsset(assetId, params = {}) {
      try {
        const response = await axios.get(`assets/${ assetId }/edit`, {params});
        return useEditForm(`assets/${ assetId }`, response.data, { onFinish: () => this.options = undefined });
      } catch (error) {
        console.error(error.response);
        throw error.response;
      }
    },
    async deleteAsset(assetId, params = {}) {
      try {
        const response = await axios.delete(`assets/${ assetId }`, {params});
        this.options = undefined;
        toasteventbus.emit('add', {
          summary: 'Asset Deleted',
          detail: `Asset has been deleted.`,
          severity: 'success',
          life: 3000
        });
      } catch (error) {
        console.error(error.response);
        throw error.response;
      }
    },
    async transferOwnership(assetId, payload = {}) {
      try {
        const response = await axios.post(`assets/${assetId}/transfer`, payload);
        toasteventbus.emit('add', {
          summary: 'Ownership Transferred',
          detail: 'Asset ownership transferred successfully.',
          severity: 'success',
          life: 3000,
        });
        // Invalidate options as company may have changed
        this.options = undefined;
        return response.data;
      } catch (error) {
        console.error(error?.response || error);
        throw (error?.response || error);
      }
    },
    async recoverAsset(assetId) {
      try {
        await axios.post(`assets/${assetId}/restore`);
        toasteventbus.emit('add', {
          summary: 'Asset Restored',
          detail: `Asset has been recovered.`,
          severity: 'success',
          life: 3000
        });
      } catch (error) {
        console.error(error.response);
        throw error.response;
      }
    },
  },
});
